import EditNoteIcon from "@mui/icons-material/EditNote";
import FunctionsIcon from "@mui/icons-material/Functions";
import LayersClearIcon from "@mui/icons-material/LayersClearOutlined";
import { Stack } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import type { PbjPlanActionProgress } from "../../../utils/models";
import { formatWholeHours } from "./planValue.utils";

type Props = {
	action: Pick<PbjPlanActionProgress, "id" | "manualHours" | "estimatedHours" | "manualHoursBeforePlan" | "manualHoursAlreadyBooked">;
};

/**
 * Manual and estimated hours, on their own row beneath an action's status line.
 *
 * Deliberately NOT on the status line: these are gross HOUR LEVELS measured quarter-to-date, while
 * everything there is delivery measured since the plan was saved — and a level sitting inches from a
 * delta reads as a contribution to it. A department whose manual hours predate its plan sees a large
 * figure here beside "Total added: 0", which is correct and was impossible to tell apart when the two
 * shared a line.
 *
 * Rounded to whole hours: context figures read at a glance, and a decimal invites them to be reconciled
 * against numbers they do not sum to.
 */
export const PlanActionContextChips: FC<Props> = ({ action }) => {
	const { t } = useTranslation();
	// DEFAULT: each `null` reads as 0 hours. Flagged per repo standards. These figures are nullable by
	// contract and `null` means "nothing of this kind to show" — exactly what 0 renders, since every chip
	// below is gated on `> 0`. Coalescing (rather than a `!== null` test) also keeps an older payload
	// that omits a field from formatting `undefined` and taking the whole tab down with it.
	const manualHours = action.manualHours ?? 0;
	const estimatedHours = action.estimatedHours ?? 0;
	const beforePlanManual = action.manualHoursBeforePlan ?? 0;
	const alreadyBookedManual = action.manualHoursAlreadyBooked ?? 0;

	if (manualHours <= 0 && estimatedHours <= 0 && alreadyBookedManual <= 0) return null;

	return (
		<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mt: 0.75 }}>
			{manualHours > 0 && (
				<MetaChip
					tone="info"
					icon={<EditNoteIcon />}
					// The pre-plan share is named on the chip itself, not buried in the tooltip: it is the whole
					// reason a four-figure manual total can sit beside a delivered figure of zero.
					label={
						beforePlanManual > 0
							? t("pbj_model.tracker.includes_manual_split", {
									value: formatWholeHours(manualHours),
									before: formatWholeHours(beforePlanManual),
								})
							: t("pbj_model.tracker.includes_manual", { value: formatWholeHours(manualHours) })
					}
					tooltip={t("pbj_model.tracker.includes_manual_tooltip")}
					dataTestId={`pbj-model-plan-action-manual-${action.id}`}
				/>
			)}
			{/* Entered but not counted. Without this the user sees the tab hold still after a write and
			    cannot tell "already counted from the Audit tab" from "my entry did not register". */}
			{alreadyBookedManual > 0 && (
				<MetaChip
					tone="warning"
					icon={<LayersClearIcon />}
					label={t("pbj_model.tracker.manual_already_booked", { value: formatWholeHours(alreadyBookedManual) })}
					tooltip={t("pbj_model.tracker.manual_already_booked_tooltip")}
					dataTestId={`pbj-model-plan-action-manual-dup-${action.id}`}
				/>
			)}
			{estimatedHours > 0 && (
				<MetaChip
					tone="info"
					icon={<FunctionsIcon />}
					label={t("pbj_model.tracker.includes_estimate", { value: formatWholeHours(estimatedHours) })}
					tooltip={t("pbj_model.tracker.includes_estimate_tooltip")}
					dataTestId={`pbj-model-plan-action-estimate-${action.id}`}
				/>
			)}
		</Stack>
	);
};
